import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'src/utils/Hooks';
import { fetchEntityMetrics } from 'src/store/modules/entitySearchService/operations';
import { mapName } from './utils';

const useAdTargetData = (adTargets: Map<string, any>) => {
  const dispatch = useAppDispatch();
  const retailerId = useAppSelector((state) => state.retailer.id);
  const mainEntity = useAppSelector((state) => state.entityService.mainEntity);
  const app = useAppSelector((state) => state.app);
  const [adTargetDocuments, setAdTargetDocuments] = useState<any[]>([]);

  useEffect(() => {
    // If there are no adTargets, clear the documents and exit early.
    if (!adTargets || adTargets.size === 0) {
      setAdTargetDocuments([]);
      return;
    }

    // Define the filters
    const excludeNegativeTargetsFilter = {
      fieldName: 'targetingType',
      condition: 'must_not',
      values: ['negativeexact', 'negativephrase']
    };

    // Build a unique set of targeting texts
    const targetingTextFilter: Set<string> = new Set();
    adTargets.forEach((target: any) => {
      targetingTextFilter.add(mapName(target?.targetingText));
    });
    if (targetingTextFilter.size === 0) {
      setAdTargetDocuments([]);
      return;
    }

    // Build the query conditions
    const queryConditions = {
      termFilters: [
        {
          fieldName: 'targetingText',
          condition: 'should',
          values: Array.from(targetingTextFilter)
        },
        {
          fieldName: 'retailerId',
          condition: 'must',
          values: [retailerId]
        }
      ],
      rangeFilters: []
    };

    // Build the overrides for the fetch call
    const overrides = [
      {
        doAggregation: false,
        conditions: {
          termFilters: [excludeNegativeTargetsFilter, ...queryConditions.termFilters],
          rangeFilters: []
        },
        pageNumber: 1,
        pageSize: 100000,
        processDocuments: false,
        returnDocuments: true
      }
    ];

    // Define and immediately invoke an async function to perform the dispatch.
    const fetchData = async () => {
      try {
        const { documents } = await dispatch(
          fetchEntityMetrics(
            'adManager_searchResultsGrid_targets',
            {
              entity: mainEntity,
              retailer: { id: retailerId },
              app,
              indexName: 'adCampaignAdGroupTargetDailyMetrics'
            },
            overrides,
            undefined,
            true
          )
        );
        setAdTargetDocuments(documents);
      } catch (error) {
        console.error('Error fetching ad target data:', error);
      }
    };

    fetchData();
  }, [adTargets, retailerId, mainEntity, app, dispatch]);

  return { adTargetDocuments };
};

export default useAdTargetData;
